import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { productService, Product } from '../services/productService';

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');
const PAGE_LIMIT = 12;

const ArrowRight: React.FC = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

const SearchIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const SearchPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const paramQ        = searchParams.get('q') ?? '';
    const paramCategory = searchParams.get('category') ?? 'All';
    const paramPage     = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));

    const [inputValue, setInputValue]   = useState(paramQ);
    const [products, setProducts]       = useState<Product[]>([]);
    const [categories, setCategories]   = useState<string[]>([]);
    const [total, setTotal]             = useState(0);
    const [loading, setLoading]         = useState(false);
    const [searched, setSearched]       = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const fetchResults = useCallback(async (q: string, category: string, page: number) => {
        if (!q.trim()) return;
        setLoading(true);
        setSearched(true);
        try {
            const query: Record<string, any> = { search: q, limit: PAGE_LIMIT, page };
            if (category !== 'All') query.category = category;
            const res = await productService.getPublicAll(query);
            setProducts(res.data.products);
            setTotal(res.data.total);
            if (res.data.categories?.length) setCategories(res.data.categories);
        } catch {
            setProducts([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setInputValue(paramQ);
        fetchResults(paramQ, paramCategory, paramPage);
    }, [paramQ, paramCategory, paramPage, fetchResults]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const q = inputValue.trim();
        if (!q) return;
        setSearchParams({ q, category: 'All', page: '1' });
    };

    const handleTabClick = (cat: string) => {
        setSearchParams({ q: paramQ, category: cat, page: '1' });
    };

    const handlePage = (p: number) => {
        setSearchParams({ q: paramQ, category: paramCategory, page: String(p) });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(total / PAGE_LIMIT) || 1;

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '48px 0 72px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container">

                {/* Breadcrumb */}
                <nav style={{ marginBottom: 28, fontSize: '0.85rem', color: '#6b7280' }}>
                    <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <Link to="/products" style={{ color: '#1565c0', textDecoration: 'none' }}>Products</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <span style={{ color: '#1a202c', fontWeight: 600 }}>Search</span>
                </nav>

                {/* Search bar */}
                <form onSubmit={handleSubmit} style={{ marginBottom: 32 }}>
                    <div style={{
                        display: 'flex',
                        gap: 0,
                        maxWidth: 560,
                        borderRadius: 10,
                        border: '1.5px solid #cbd5e1',
                        overflow: 'hidden',
                        background: '#fff',
                        boxShadow: '0 2px 8px rgba(21,101,192,0.07)',
                    }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            placeholder="Search pom poms, tassels, laces…"
                            style={{
                                flex: 1,
                                border: 'none',
                                outline: 'none',
                                padding: '12px 16px',
                                fontSize: '0.95rem',
                                color: '#1a202c',
                                background: 'transparent',
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: '0 20px',
                                background: '#1565c0',
                                border: 'none',
                                color: '#fff',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <SearchIcon /> Search
                        </button>
                    </div>
                </form>

                {/* Heading */}
                {searched && (
                    <div style={{ marginBottom: 28 }}>
                        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a202c', margin: '0 0 4px' }}>
                            {loading ? 'Searching…' : (
                                total > 0
                                    ? <>Results for <em style={{ color: '#1565c0', fontStyle: 'normal' }}>"{paramQ}"</em></>
                                    : <>No results for <em style={{ color: '#1565c0', fontStyle: 'normal' }}>"{paramQ}"</em></>
                            )}
                        </h1>
                        {!loading && total > 0 && (
                            <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                                {total} product{total !== 1 ? 's' : ''} found
                                {paramCategory !== 'All' ? ` in ${paramCategory}` : ''}
                            </p>
                        )}
                    </div>
                )}

                {/* Category filter tabs — shown only when results exist */}
                {searched && !loading && total > 0 && categories.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
                        {['All', ...categories].map(cat => (
                            <button
                                key={cat}
                                onClick={() => handleTabClick(cat)}
                                style={{
                                    padding: '7px 20px',
                                    borderRadius: 50,
                                    border: `1.5px solid ${paramCategory === cat ? '#1565c0' : '#d1d5db'}`,
                                    background: paramCategory === cat ? '#1565c0' : '#fff',
                                    color: paramCategory === cat ? '#fff' : '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* Results grid */}
                {!searched && !loading && (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                        <SearchIcon />
                        <p style={{ marginTop: 16, fontSize: '1rem' }}>Type something to search our product catalogue.</p>
                        <Link to="/products" style={{ color: '#1565c0', fontWeight: 600, textDecoration: 'none' }}>
                            Browse all products →
                        </Link>
                    </div>
                )}

                {searched && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: 24,
                        marginBottom: 48,
                    }}>
                        {loading
                            ? Array.from({ length: PAGE_LIMIT }).map((_, i) => (
                                <div key={i} style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', background: '#fff' }}>
                                    <div style={{ aspectRatio: '1/1', background: 'linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%)', backgroundSize: '200% 100%', animation: 'pcat-shimmer 1.4s infinite' }} />
                                    <div style={{ padding: 16 }}>
                                        <div style={{ height: 10, borderRadius: 5, background: '#e2e8f0', marginBottom: 8, width: '40%' }} />
                                        <div style={{ height: 12, borderRadius: 5, background: '#e2e8f0', marginBottom: 6, width: '80%' }} />
                                        <div style={{ height: 10, borderRadius: 5, background: '#e2e8f0', width: '55%' }} />
                                    </div>
                                </div>
                            ))
                            : products.length === 0
                                ? (
                                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                        <p style={{ fontSize: '1rem', marginBottom: 16 }}>
                                            No products matched your search. Try different keywords.
                                        </p>
                                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => { setInputValue(''); inputRef.current?.focus(); }}
                                                style={{ color: '#1565c0', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem' }}
                                            >
                                                Clear search
                                            </button>
                                            <span style={{ color: '#d1d5db' }}>|</span>
                                            <Link to="/products" style={{ color: '#1565c0', fontWeight: 600, textDecoration: 'none' }}>
                                                Browse all products →
                                            </Link>
                                        </div>
                                    </div>
                                )
                                : products.map(p => <ProductCard key={p._id} product={p} />)
                        }
                    </div>
                )}

                {/* Pagination */}
                {searched && !loading && totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                        <button
                            onClick={() => handlePage(paramPage - 1)}
                            disabled={paramPage <= 1}
                            style={paginBtn(paramPage <= 1)}
                        >
                            ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - paramPage) <= 2)
                            .map((p, idx, arr) => (
                                <React.Fragment key={p}>
                                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                                        <span style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>
                                    )}
                                    <button
                                        onClick={() => handlePage(p)}
                                        style={paginBtn(false, p === paramPage)}
                                    >
                                        {p}
                                    </button>
                                </React.Fragment>
                            ))
                        }
                        <button
                            onClick={() => handlePage(paramPage + 1)}
                            disabled={paramPage >= totalPages}
                            style={paginBtn(paramPage >= totalPages)}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

function paginBtn(disabled: boolean, active?: boolean): React.CSSProperties {
    return {
        padding: '7px 14px',
        borderRadius: 8,
        border: `1.5px solid ${active ? '#1565c0' : '#d1d5db'}`,
        background: active ? '#1565c0' : disabled ? '#f9fafb' : '#fff',
        color: active ? '#fff' : disabled ? '#9ca3af' : '#374151',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
    };
}

const ProductCard: React.FC<{ product: Product }> = ({ product: p }) => (
    <div
        style={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'box-shadow 0.2s, transform 0.2s',
        }}
        onMouseEnter={e => {
            (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(21,101,192,0.12)';
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
        }}
        onMouseLeave={e => {
            (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        }}
    >
        <div style={{ aspectRatio: '1/1', overflow: 'hidden', background: '#f8fafc' }}>
            <img
                src={`${API_STATIC}${p.image}`}
                alt={p.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => {
                    (e.target as HTMLImageElement).src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f0f4f8"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';
                }}
            />
        </div>
        <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                {p.category}
            </span>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1a202c', margin: '0 0 2px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {p.name}
            </h3>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 12px' }}>{p.brand}</p>
            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a202c' }}>
                    ₹{p.price.toLocaleString('en-IN')}
                </span>
                <Link
                    to={`/products/${p._id}`}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '6px 14px', background: '#1565c0', color: '#fff',
                        borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
                        textDecoration: 'none',
                    }}
                >
                    View <ArrowRight />
                </Link>
            </div>
        </div>
    </div>
);

export default SearchPage;
